import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft, User, MapPin, Users, DollarSign, Store, Calendar,
  Globe, Utensils, Check, X, Camera, Save, Loader2, Crown, Clock, AlertTriangle,
  Ban, Trash2, Lock, KeyRound, Mail, Bell, UtensilsCrossed
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIETARY_OPTIONS = [
  "Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Keto",
  "Paleo", "Halal", "Kosher", "Nut-Free", "Low-Sodium", "None",
];

const ALLERGY_OPTIONS = [
  "Peanuts", "Tree Nuts", "Dairy", "Eggs", "Wheat/Gluten",
  "Soy", "Fish", "Shellfish", "Sesame",
];

const POPULAR_STORES = [
  "Walmart", "Kroger", "Aldi", "Costco", "Target", "Publix",
  "Safeway", "Meijer", "H-E-B", "Whole Foods", "Trader Joe's",
  "Food Lion", "ShopRite", "Wegmans", "Sam's Club", "Albertsons",
  "Lidl", "Sprouts", "WinCo", "Hy-Vee",
];

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 Weeks" },
  { value: "monthly", label: "Monthly" },
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "zh", label: "Chinese" },
  { value: "ko", label: "Korean" },
  { value: "vi", label: "Vietnamese" },
];

const Section = ({ icon: Icon, title, children }) => (
  <Card>
    <CardContent className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon className="h-3.5 w-3.5 text-green-600" />
        </div>
        <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
      </div>
      {children}
    </CardContent>
  </Card>
);

const CredentialChanger = ({ token, toast }) => {
  const [activeForm, setActiveForm] = useState(null); // "pin" | "password" | "email"
  const [formLoading, setFormLoading] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPinVal, setNewPinVal] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPasswordVal, setNewPasswordVal] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [newEmailVal, setNewEmailVal] = useState("");

  const resetForm = () => {
    setActiveForm(null);
    setCurrentPin(""); setNewPinVal(""); setCurrentPassword("");
    setNewPasswordVal(""); setConfirmPin(""); setNewEmailVal("");
  };

  const handleChangePin = async () => {
    setFormLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_pin: currentPin, new_pin: newPinVal }),
      });
      const d = await res.json();
      if (res.ok) { toast({ title: "PIN Updated", description: d.message }); resetForm(); }
      else toast({ title: "Error", description: d.detail, variant: "destructive" });
    } catch { toast({ title: "Error", description: "Something went wrong", variant: "destructive" }); }
    setFormLoading(false);
  };

  const handleChangePassword = async () => {
    setFormLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPasswordVal }),
      });
      const d = await res.json();
      if (res.ok) { toast({ title: "Password Updated", description: d.message }); resetForm(); }
      else toast({ title: "Error", description: d.detail, variant: "destructive" });
    } catch { toast({ title: "Error", description: "Something went wrong", variant: "destructive" }); }
    setFormLoading(false);
  };

  const handleChangeEmail = async () => {
    setFormLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pin: confirmPin, new_email: newEmailVal }),
      });
      const d = await res.json();
      if (res.ok) { toast({ title: "Email Updated", description: d.message }); resetForm(); }
      else toast({ title: "Error", description: d.detail, variant: "destructive" });
    } catch { toast({ title: "Error", description: "Something went wrong", variant: "destructive" }); }
    setFormLoading(false);
  };

  const buttons = [
    { id: "pin", label: "Change PIN", icon: KeyRound },
    { id: "password", label: "Change Password", icon: Lock },
    { id: "email", label: "Change Email", icon: Mail },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {buttons.map(b => (
          <button
            key={b.id}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeForm === b.id ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => { resetForm(); setActiveForm(activeForm === b.id ? null : b.id); }}
            data-testid={`settings-${b.id}-btn`}
          >
            <b.icon className="h-3 w-3" /> {b.label}
          </button>
        ))}
      </div>

      {activeForm === "pin" && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-2 border" data-testid="change-pin-form">
          <Input type="password" inputMode="numeric" maxLength={6} placeholder="Current PIN" value={currentPin} onChange={e => setCurrentPin(e.target.value.replace(/\D/g, ""))} className="text-sm h-9" data-testid="current-pin-input" />
          <Input type="password" inputMode="numeric" maxLength={6} placeholder="New PIN (4+ digits)" value={newPinVal} onChange={e => setNewPinVal(e.target.value.replace(/\D/g, ""))} className="text-sm h-9" data-testid="new-pin-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={resetForm}>Cancel</Button>
            <Button size="sm" className="text-xs h-8 bg-green-500 hover:bg-green-600 text-white" onClick={handleChangePin} disabled={formLoading || !currentPin || newPinVal.length < 4} data-testid="save-pin-btn">
              {formLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save PIN"}
            </Button>
          </div>
        </div>
      )}

      {activeForm === "password" && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-2 border" data-testid="change-password-form">
          <Input type="password" placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="text-sm h-9" data-testid="current-password-input" />
          <Input type="password" placeholder="New password (6+ chars)" value={newPasswordVal} onChange={e => setNewPasswordVal(e.target.value)} className="text-sm h-9" data-testid="new-password-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={resetForm}>Cancel</Button>
            <Button size="sm" className="text-xs h-8 bg-green-500 hover:bg-green-600 text-white" onClick={handleChangePassword} disabled={formLoading || !currentPassword || newPasswordVal.length < 6} data-testid="save-password-btn">
              {formLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save Password"}
            </Button>
          </div>
        </div>
      )}

      {activeForm === "email" && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-2 border" data-testid="change-email-form">
          <Input type="email" placeholder="New email address" value={newEmailVal} onChange={e => setNewEmailVal(e.target.value)} className="text-sm h-9" data-testid="new-email-input" />
          <Input type="password" inputMode="numeric" maxLength={6} placeholder="Enter PIN to confirm" value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ""))} className="text-sm h-9" data-testid="confirm-pin-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={resetForm}>Cancel</Button>
            <Button size="sm" className="text-xs h-8 bg-green-500 hover:bg-green-600 text-white" onClick={handleChangeEmail} disabled={formLoading || !newEmailVal || !confirmPin} data-testid="save-email-btn">
              {formLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save Email"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { user, token, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [name, setName] = useState(user?.name || "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const [dietary, setDietary] = useState(user?.dietary_preferences || []);
  const [allergies, setAllergies] = useState(user?.allergies || []);
  const [city, setCity] = useState(user?.city || "");
  const [stateName, setStateName] = useState(user?.state || "");
  const [zipCode, setZipCode] = useState(user?.zip_code || "");
  const [householdSize, setHouseholdSize] = useState(String(user?.household_size || "2"));
  const [groceryBudget, setGroceryBudget] = useState(String(user?.grocery_budget || ""));
  const [preferredStores, setPreferredStores] = useState(user?.preferred_stores || []);
  const [customStore, setCustomStore] = useState("");
  const [frequency, setFrequency] = useState(user?.shopping_frequency || "weekly");
  const [language, setLanguage] = useState(user?.language || "en");
  // Alert times — per-user local timezone + preferred windows
  const _guessedTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "America/New_York"; }
  })();
  const _defaultAlerts = {
    timezone: _guessedTz,
    deals_morning: "08:00",
    deals_evening: "17:00",
    meal_breakfast: "07:00",
    meal_dinner: "16:00",
    deals_enabled: true,
    meals_enabled: true,
  };
  const [alertPrefs, setAlertPrefs] = useState({ ..._defaultAlerts, ...(user?.alert_preferences || {}) });
  const setAlert = (k, v) => setAlertPrefs(prev => ({ ...prev, [k]: v }));
  const [saving, setSaving] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Load subscription status
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSubStatus(await res.json());
      } catch (e) { /* ignore */ }
    };
    load();
  }, [token]);

  const toggleDietary = (opt) => {
    if (opt === "None") {
      setDietary(prev => prev.includes("None") ? [] : ["None"]);
    } else {
      setDietary(prev =>
        prev.filter(d => d !== "None").includes(opt)
          ? prev.filter(d => d !== opt)
          : [...prev.filter(d => d !== "None"), opt]
      );
    }
  };

  const toggleStore = (store) => {
    setPreferredStores(prev =>
      prev.includes(store) ? prev.filter(s => s !== store) : [...prev, store]
    );
  };

  const addCustomStore = () => {
    const s = customStore.trim();
    if (s && !preferredStores.includes(s)) setPreferredStores(prev => [...prev, s]);
    setCustomStore("");
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/community/upload`, { method: "POST", body: formData });
      if (res.ok) {
        const data = await res.json();
        setAvatarUrl(data.url || data.file_url);
      }
    } catch (err) { /* ignore */ }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await updateProfile({
      name: name.trim(),
      avatar_url: avatarUrl,
      dietary_preferences: dietary.filter(d => d !== "None"),
      allergies,
      city: city.trim(),
      state: stateName.trim(),
      zip_code: zipCode.trim(),
      household_size: parseInt(householdSize) || 1,
      grocery_budget: parseFloat(groceryBudget) || 0,
      preferred_stores: preferredStores,
      shopping_frequency: frequency,
      language,
      alert_preferences: alertPrefs,
    });
    setSaving(false);
    toast({ title: "Settings Saved", description: "Your profile has been updated." });
  };

  const isPremium = subStatus && (subStatus.plan === "monthly" || subStatus.plan === "annual");
  const isTrial = subStatus?.plan === "trial";
  const isFree = subStatus?.plan === "free";
  const canCancel = isPremium || isTrial;

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast({ title: "Subscription Cancelled", description: "You've been moved to the free plan." });
        setSubStatus({ plan: "free", status: "cancelled", days_left: 0 });
        setShowCancelConfirm(false);
      } else {
        const err = await res.json();
        toast({ title: "Error", description: err.detail || "Failed to cancel", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    }
    setActionLoading(false);
  };

  const handleDeleteAccount = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast({ title: "Account Deleted", description: "Your account has been removed." });
        setTimeout(() => logout(), 1500);
      } else {
        const err = await res.json();
        toast({ title: "Error", description: err.detail || "Failed to delete account", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    }
    setActionLoading(false);
  };

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-full" data-testid="settings-back-btn">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Settings</h2>
          <p className="text-sm text-gray-500">Manage your profile & preferences</p>
        </div>
      </div>

      {/* Subscription Status */}
      {subStatus && (
        <Card className={isPremium ? "border-green-300 bg-green-50" : isTrial ? "border-blue-300 bg-blue-50" : "border-amber-300 bg-amber-50"} data-testid="settings-subscription-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPremium ? "bg-green-200" : isTrial ? "bg-blue-200" : "bg-amber-200"}`}>
                  <Crown className={`h-5 w-5 ${isPremium ? "text-green-700" : isTrial ? "text-blue-700" : "text-amber-700"}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm" data-testid="settings-plan-name">
                    {isPremium ? `${subStatus.plan.charAt(0).toUpperCase() + subStatus.plan.slice(1)} Plan` : isTrial ? "Free Trial" : "Free Plan"}
                  </p>
                  {isTrial && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3 text-blue-600" />
                      <p className="text-xs text-blue-700 font-medium" data-testid="settings-trial-countdown">
                        {subStatus.days_left} day{subStatus.days_left !== 1 ? "s" : ""} remaining
                      </p>
                    </div>
                  )}
                  {isPremium && (
                    <p className="text-xs text-green-700 mt-0.5" data-testid="settings-sub-active">Active subscription</p>
                  )}
                  {isFree && (
                    <p className="text-xs text-amber-700 mt-0.5" data-testid="settings-sub-free">Limited access — Home tab only</p>
                  )}
                </div>
              </div>
              {!isPremium && (
                <Button
                  size="sm"
                  className={`text-xs h-8 ${isTrial ? "bg-blue-500 hover:bg-blue-600" : "bg-amber-500 hover:bg-amber-600"} text-white`}
                  onClick={() => navigate("/subscription")}
                  data-testid="settings-upgrade-btn"
                >
                  {isTrial ? "View Plans" : "Upgrade"}
                </Button>
              )}
            </div>
            {isTrial && subStatus.trial_end && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.max(0, Math.min(100, (subStatus.days_left / 14) * 100))}%` }}
                  />
                </div>
                <p className="text-[10px] text-blue-600 mt-1">
                  Trial ends {new Date(subStatus.trial_end).toLocaleDateString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Profile */}
      <Section icon={User} title="Profile">
        <div className="flex items-center gap-4">
          <div className="relative">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-green-400 bg-gray-100" data-testid="settings-avatar" />
            ) : (
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <User className="h-7 w-7 text-white" />
              </div>
            )}
            <button
              className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-sm hover:bg-green-600"
              onClick={() => fileRef.current?.click()}
              data-testid="change-avatar-btn"
            >
              <Camera className="h-3 w-3 text-white" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 font-medium block mb-1">Display Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="settings-name" />
          </div>
        </div>
        {uploading && <p className="text-xs text-green-600">Uploading avatar...</p>}
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Email</label>
          <Input value={user?.email || ""} disabled className="bg-gray-50 text-gray-500" data-testid="settings-email" />
        </div>
      </Section>

      {/* Dietary */}
      <Section icon={Utensils} title="Dietary Preferences">
        <div className="flex flex-wrap gap-1.5">
          {DIETARY_OPTIONS.map(opt => (
            <button
              key={opt}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                dietary.includes(opt) ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => toggleDietary(opt)}
              data-testid={`settings-dietary-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {dietary.includes(opt) && <Check className="h-2.5 w-2.5 inline mr-0.5" />}
              {opt}
            </button>
          ))}
        </div>
      </Section>

      {/* Allergies */}
      <Section icon={AlertTriangle} title="Food Allergies">
        <div className="flex flex-wrap gap-1.5">
          {ALLERGY_OPTIONS.map(allergy => (
            <button
              key={allergy}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                allergies.includes(allergy) ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setAllergies(prev => prev.includes(allergy) ? prev.filter(a => a !== allergy) : [...prev, allergy])}
              data-testid={`settings-allergy-${allergy.toLowerCase().replace(/[\s/]+/g, "-")}`}
            >
              {allergies.includes(allergy) && <Check className="h-2.5 w-2.5 inline mr-0.5" />}
              {allergy}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">Recipes and meal plans will exclude these allergens.</p>
      </Section>

      {/* Location */}
      <Section icon={MapPin} title="Location">
        <Input placeholder="City" value={city} onChange={e => setCity(e.target.value)} data-testid="settings-city" />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="State" value={stateName} onChange={e => setStateName(e.target.value)} data-testid="settings-state" />
          <Input placeholder="Zip Code" value={zipCode} onChange={e => setZipCode(e.target.value)} data-testid="settings-zip" />
        </div>
      </Section>

      {/* Household & Budget */}
      <Section icon={Users} title="Household & Budget">
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Household Size</label>
          <div className="flex gap-2">
            {["1", "2", "3", "4", "5", "6+"].map(s => (
              <button
                key={s}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  householdSize === s ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => setHouseholdSize(s)}
                data-testid={`settings-household-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Monthly Grocery Budget</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="number"
              placeholder="e.g. 600"
              value={groceryBudget}
              onChange={e => setGroceryBudget(e.target.value)}
              className="pl-9"
              data-testid="settings-budget"
            />
          </div>
        </div>
      </Section>

      {/* Preferred Stores */}
      <Section icon={Store} title="Preferred Stores">
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {POPULAR_STORES.map(store => (
            <button
              key={store}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                preferredStores.includes(store) ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => toggleStore(store)}
              data-testid={`settings-store-${store.toLowerCase().replace(/[\s']+/g, "-")}`}
            >
              {preferredStores.includes(store) && <Check className="h-2.5 w-2.5 inline mr-0.5" />}
              {store}
            </button>
          ))}
        </div>
        {preferredStores.filter(s => !POPULAR_STORES.includes(s)).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {preferredStores.filter(s => !POPULAR_STORES.includes(s)).map(store => (
              <button
                key={store}
                className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-500 text-white"
                onClick={() => toggleStore(store)}
              >
                <Check className="h-2.5 w-2.5 inline mr-0.5" />{store} <X className="h-2.5 w-2.5 inline ml-0.5" />
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            placeholder="Add a local store..."
            value={customStore}
            onChange={e => setCustomStore(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustomStore())}
            className="text-sm h-8"
            data-testid="settings-custom-store"
          />
          <Button
            type="button"
            size="sm"
            className="bg-green-500 hover:bg-green-600 text-white h-8 px-3 text-xs"
            onClick={addCustomStore}
            disabled={!customStore.trim()}
            data-testid="settings-add-store"
          >
            Add
          </Button>
        </div>
      </Section>

      {/* Shopping Frequency */}
      <Section icon={Calendar} title="Shopping Frequency">
        <div className="grid grid-cols-2 gap-2">
          {FREQUENCY_OPTIONS.map(f => (
            <button
              key={f.value}
              className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                frequency === f.value ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setFrequency(f.value)}
              data-testid={`settings-freq-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Section>


      {/* Alert Times — per-user timezone-aware notification windows */}
      <Section icon={Bell} title="Alert Times">
        <p className="text-xs text-gray-500 mb-3">
          Choose when you&apos;d like to receive deal alerts and meal suggestions in
          your local time. Alerts fire twice daily — morning & evening — so you can
          plan around your family&apos;s routine.
        </p>

        {/* Timezone */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
          <select
            value={alertPrefs.timezone}
            onChange={(e) => setAlert("timezone", e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white"
            data-testid="alert-timezone"
          >
            {[
              "America/New_York", "America/Chicago", "America/Denver",
              "America/Phoenix", "America/Los_Angeles", "America/Anchorage",
              "Pacific/Honolulu", "America/Toronto", "America/Vancouver",
              "America/Mexico_City", "UTC",
            ].map(tz => (
              <option key={tz} value={tz}>{tz.replace("_", " ")}</option>
            ))}
            {/* Include the auto-detected one if it's not in the list above */}
            {!["America/New_York","America/Chicago","America/Denver","America/Phoenix","America/Los_Angeles","America/Anchorage","Pacific/Honolulu","America/Toronto","America/Vancouver","America/Mexico_City","UTC"].includes(alertPrefs.timezone) && (
              <option value={alertPrefs.timezone}>{alertPrefs.timezone}</option>
            )}
          </select>
        </div>

        {/* Deal Alerts */}
        <div className="border border-gray-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-600" />
              <p className="text-sm font-semibold text-gray-800">Deal Alerts</p>
            </div>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!alertPrefs.deals_enabled}
                onChange={(e) => setAlert("deals_enabled", e.target.checked)}
                className="h-4 w-4"
                data-testid="alert-deals-enabled"
              />
              <span className="text-xs text-gray-600">{alertPrefs.deals_enabled ? "On" : "Off"}</span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            When items on your grocery list — or cheaper Smart Alternatives — go on sale nearby.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Morning</label>
              <input
                type="time"
                value={alertPrefs.deals_morning}
                onChange={(e) => setAlert("deals_morning", e.target.value)}
                disabled={!alertPrefs.deals_enabled}
                className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:bg-gray-100 disabled:text-gray-400"
                data-testid="alert-deals-morning"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Evening</label>
              <input
                type="time"
                value={alertPrefs.deals_evening}
                onChange={(e) => setAlert("deals_evening", e.target.value)}
                disabled={!alertPrefs.deals_enabled}
                className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:bg-gray-100 disabled:text-gray-400"
                data-testid="alert-deals-evening"
              />
            </div>
          </div>
        </div>

        {/* Meal Suggestions */}
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4 text-emerald-600" />
              <p className="text-sm font-semibold text-gray-800">Meal Suggestions</p>
            </div>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!alertPrefs.meals_enabled}
                onChange={(e) => setAlert("meals_enabled", e.target.checked)}
                className="h-4 w-4"
                data-testid="alert-meals-enabled"
              />
              <span className="text-xs text-gray-600">{alertPrefs.meals_enabled ? "On" : "Off"}</span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            AI answers &quot;what&apos;s for breakfast?&quot; and &quot;what&apos;s for dinner?&quot; using your
            diet, allergies & recent grocery habits.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Breakfast</label>
              <input
                type="time"
                value={alertPrefs.meal_breakfast}
                onChange={(e) => setAlert("meal_breakfast", e.target.value)}
                disabled={!alertPrefs.meals_enabled}
                className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:bg-gray-100 disabled:text-gray-400"
                data-testid="alert-meal-breakfast"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">Dinner</label>
              <input
                type="time"
                value={alertPrefs.meal_dinner}
                onChange={(e) => setAlert("meal_dinner", e.target.value)}
                disabled={!alertPrefs.meals_enabled}
                className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 disabled:bg-gray-100 disabled:text-gray-400"
                data-testid="alert-meal-dinner"
              />
            </div>
          </div>
        </div>
      </Section>


      {/* Security & Credentials */}
      <Card data-testid="security-section">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Lock className="h-3.5 w-3.5 text-green-600" />
            </div>
            <h3 className="font-semibold text-sm text-gray-900">Security</h3>
          </div>

          <CredentialChanger token={token} toast={toast} />
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 bg-red-50/50" data-testid="danger-zone">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
            </div>
            <h3 className="font-semibold text-sm text-red-800">Danger Zone</h3>
          </div>

          {/* Cancel Subscription */}
          {canCancel && (
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
              <div>
                <p className="text-sm font-medium text-gray-900">Cancel Subscription</p>
                <p className="text-xs text-gray-500">Revert to the free plan. You'll lose premium features.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 text-xs h-8"
                onClick={() => setShowCancelConfirm(true)}
                data-testid="cancel-subscription-btn"
              >
                <Ban className="h-3 w-3 mr-1" /> Cancel
              </Button>
            </div>
          )}

          {/* Delete Account */}
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Delete Account</p>
              <p className="text-xs text-gray-500">Permanently remove your account and all data.</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-red-400 text-red-700 hover:bg-red-100 hover:text-red-800 text-xs h-8"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-account-btn"
            >
              <Trash2 className="h-3 w-3 mr-1" /> Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cancel-confirm-modal">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 space-y-4 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Ban className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Cancel Subscription?</h3>
                <p className="text-xs text-gray-500">This cannot be undone immediately</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              You'll be moved to the <strong>free plan</strong> and lose access to premium features like Meal Plans, Recipes, Deals, and more. You can re-subscribe anytime.
            </p>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-9 text-sm"
                onClick={() => setShowCancelConfirm(false)}
                disabled={actionLoading}
                data-testid="cancel-confirm-no"
              >
                Keep My Plan
              </Button>
              <Button
                className="flex-1 h-9 text-sm bg-red-500 hover:bg-red-600 text-white"
                onClick={handleCancelSubscription}
                disabled={actionLoading}
                data-testid="cancel-confirm-yes"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, Cancel"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 space-y-4 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Your Account?</h3>
                <p className="text-xs text-gray-500">This action is permanent</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Your account, grocery lists, meal plans, and all associated data will be permanently deleted. This <strong>cannot be undone</strong>.
            </p>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm
              </label>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="text-sm"
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                className="flex-1 h-9 text-sm"
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                disabled={actionLoading}
                data-testid="delete-confirm-no"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-9 text-sm bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteAccount}
                disabled={actionLoading || deleteConfirmText !== "DELETE"}
                data-testid="delete-confirm-yes"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete Forever"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Legal Links */}
      <div className="text-center space-y-1 pb-2">
        <div className="flex justify-center gap-4 text-xs text-gray-400">
          <a href="/privacy" className="underline hover:text-gray-500" data-testid="settings-privacy-link">Privacy Policy</a>
          <a href="/terms" className="underline hover:text-gray-500" data-testid="settings-terms-link">Terms of Service</a>
        </div>
        <p className="text-[10px] text-gray-300">support@foodzlizts.com</p>
      </div>

      {/* Save Button - Sticky */}
      <div className="fixed bottom-16 left-0 right-0 px-4 pb-2">
        <Button
          className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold shadow-lg"
          onClick={handleSave}
          disabled={saving}
          data-testid="settings-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
